import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { FlaskConical, Moon, Trophy, Sparkles, Eye } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Each source uses its own pill color so the bar reads like a tiny dashboard.
const SOURCE_META = (isDe) => ({
    lab: {
        icon: FlaskConical,
        label: isDe ? 'Laborwerte' : 'Lab values',
        bar: 'bg-violet-500',
        text: 'text-violet-700 dark:text-violet-300',
    },
    sleep: {
        icon: Moon,
        label: isDe ? 'Schlaf' : 'Sleep',
        bar: 'bg-indigo-500',
        text: 'text-indigo-700 dark:text-indigo-300',
    },
    goal: {
        icon: Trophy,
        label: isDe ? 'Quiz-Ziel' : 'Quiz goal',
        bar: 'bg-amber-500',
        text: 'text-amber-700 dark:text-amber-300',
    },
    static: {
        icon: Sparkles,
        label: isDe ? 'Allgemein' : 'General',
        bar: 'bg-emerald-500',
        text: 'text-emerald-700 dark:text-emerald-300',
    },
});

// Returns a human "Nanouk lernt …" line based on the dominant source.
function trustNarrative(counts, isDe) {
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    if (total === 0) {
        return isDe
            ? 'Öffne mal das (i) auf einem Quick-Prompt im Coach — Nanouk merkt sich, was du verstehen willst.'
            : 'Tap the (i) icon on any Coach quick-prompt — Nanouk will remember what you want to understand.';
    }
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    const [src, count] = top;
    const pct = Math.round((count / total) * 100);
    const map = {
        lab: {
            de: `Du vertraust **Laborwerten am meisten** (${pct} % deiner Insights). Nanouk priorisiert künftig konkrete Marker-Korrelationen.`,
            en: `You trust **lab values the most** (${pct} % of your insights). Nanouk will prioritize concrete marker correlations.`,
        },
        sleep: {
            de: `**Schlaf-Insights** stehen bei dir oben (${pct} %). Nanouk wird häufiger schlafbezogene Vorschläge bringen.`,
            en: `**Sleep insights** lead your reading list (${pct} %). Nanouk will lean into sleep-related suggestions.`,
        },
        goal: {
            de: `Dein **Quiz-Ziel** zieht dich am meisten (${pct} %). Nanouk hält den langfristigen Plan aktiv im Hinterkopf.`,
            en: `Your **quiz goal** pulls you in most (${pct} %). Nanouk will keep your long-term plan front-of-mind.`,
        },
        static: {
            de: `Du klickst noch oft **Standard-Prompts** (${pct} %). Lade einen Befund hoch oder logge eine Woche Schlaf — dann wird's persönlich.`,
            en: `You're still mostly tapping **default prompts** (${pct} %). Upload a lab or log a week of sleep — then it gets personal.`,
        },
    };
    return (map[src]?.[isDe ? 'de' : 'en']) || '';
}

/**
 * "Trust Mirror" card on /achievements. Shows the user how often they've
 * read each personalization type (lab/sleep/goal/static) and what Nanouk
 * is learning from it. Pure transparency surface — no new data is logged
 * by this view, it only reads /api/coach/insight-streak.
 */
export function TrustMirrorCard() {
    const { language } = useLanguage();
    const isDe = language === 'de';
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/coach/insight-streak`);
                if (!cancelled) setData(res.data);
            } catch (_e) { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!data) return null;

    const counts = data.source_counts || {};
    const sources = SOURCE_META(isDe);
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    // Fixed source-type order — keeps colours stable across renders.
    const activeSourceKeys = ['lab', 'sleep', 'goal', 'static'].filter((k) => (counts[k] || 0) > 0);
    if (total === 0 && (data.streak || 0) === 0) {
        // Show a gentle empty-state once, not a screaming card.
        return (
            <Card className="border-dashed bg-muted/20" data-testid="trust-mirror-card">
                <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-violet-500/10 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                        <Eye className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div>
                        <p className="font-semibold text-sm">
                            {isDe ? 'Trust-Mirror' : 'Trust Mirror'}
                        </p>
                        <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                            {trustNarrative(counts, isDe)}
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const narrative = trustNarrative(counts, isDe);

    return (
        <Card className="border-violet-500/20 bg-gradient-to-br from-violet-500/5 to-cyan-500/5" data-testid="trust-mirror-card">
            <CardContent className="p-4 space-y-3">
                {/* Header */}
                <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                    <p className="font-semibold text-sm">
                        {isDe ? 'Trust-Mirror' : 'Trust Mirror'}
                    </p>
                    <span className="ml-auto text-[10px] text-muted-foreground">
                        {isDe ? `${total} Insights gelesen` : `${total} insights read`}
                    </span>
                </div>

                {/* Stacked bar */}
                <div className="flex h-3 rounded-full overflow-hidden bg-muted/40" data-testid="trust-mirror-bar">
                    {activeSourceKeys.map((k) => {
                        const meta = sources[k];
                        const pct = Math.round(((counts[k] || 0) / total) * 100);
                        return (
                            <div
                                key={k}
                                className={meta.bar}
                                style={{ width: `${pct}%` }}
                                aria-label={`${meta.label}: ${pct}%`}
                                title={`${meta.label} · ${pct}%`}
                                data-testid={`trust-mirror-segment-${k}`}
                            />
                        );
                    })}
                </div>

                {/* Legend */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {activeSourceKeys.map((k) => {
                        const meta = sources[k];
                        const Icon = meta.icon;
                        const count = counts[k] || 0;
                        const pct = Math.round((count / total) * 100);
                        return (
                            <div key={k} className="flex items-center gap-1.5" data-testid={`trust-mirror-legend-${k}`}>
                                <Icon className={`w-3.5 h-3.5 ${meta.text}`} />
                                <span className={`text-[11px] font-semibold ${meta.text}`}>{count}</span>
                                <span className="text-[10px] text-muted-foreground">{pct}%</span>
                            </div>
                        );
                    })}
                </div>

                {/* Narrative */}
                <p
                    className="text-xs leading-relaxed text-muted-foreground"
                    data-testid="trust-mirror-narrative"
                    // Render the **bold** markers as <strong>. Plain string -> safe.
                    dangerouslySetInnerHTML={{
                        __html: narrative.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-foreground">$1</strong>'),
                    }}
                />

                {/* Streak progress hint */}
                {(data.streak || 0) > 0 && !data.badge_unlocked && (
                    <p className="text-[10px] text-muted-foreground pt-1 border-t border-border/40">
                        🧠 {isDe
                            ? `Buddy-Brain Fortschritt: ${data.streak} von ${data.streak_target} Tagen`
                            : `Buddy-Brain progress: ${data.streak} of ${data.streak_target} days`}
                    </p>
                )}
                {data.badge_unlocked && (
                    <p className="text-[10px] font-medium text-amber-600 dark:text-amber-400 pt-1 border-t border-border/40">
                        🧠 {isDe
                            ? `Buddy-Brain freigeschaltet · längster Streak: ${data.best_streak}d`
                            : `Buddy-Brain unlocked · best streak: ${data.best_streak}d`}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}
